import {Spec} from 'immutability-helper';
import _ from 'underscore';

import {DataModificationsHeader} from 'audit/DataModifications';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {DocumentUpdateSpec, EventConstants} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import {
  Document,
  FromServerDocument,
  ToServerDocumentWithMetadata,
} from 'notebook/documents/DocumentConstants';
import DocumentDAO, {DocumentsByVersion} from 'notebook/documents/DocumentDAO';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {CidIdMap, CidNotePartProvenanceIdMap} from 'notebook/notes/NoteConstants';
import {ServerDocumentEditorLock} from 'notebook/realtime/DocumentEditorLockConstants';
import AutoSaver from 'notebook/store/auto-save/AutoSaver';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import * as net from 'util/Net';
import pick from 'util/pick';

export interface DocumentUpdateOptions {
  structuredTableMappings?: Array<StructuredTableMapping>;
  updatedItemPartIndexes?: Array<number>;
  canIncrementalSave?: boolean;
  performTableEvaluation?: boolean;
}

export interface DocumentOpenOptions {
  // Allows the document to be immediately patched if evaluation runs on open.
  bumpVersion?: boolean;
  // Disables the internal autosaver for this document.
  disableAutoSaver?: boolean;
  // Provides a custom AutoSaver instance to use instead of creating a new one.
  customAutoSaver?: AutoSaver | null;
  // Provides a custom save handler to use instead of the default save behavior.
  customSaveHandler?: () => Promise<boolean>;
  /**
   * A function that provides the related document ids for a document. This is important to provide when
   * allowing Cross-Step Data Refs so that there are an initial set of related document ids for
   * the document.
   * */
  relatedDocumentIdGetter?: () => Array<string>;
}

/**
 * This function handles dispatching the appropriate events after a DocumentDAO.(save|saveMany) response is
 * returned. It handles dispatching the appropriate signals for each of the Documents to the store. It is
 * intended to be used as part of a Promise chain.
 */
function processDocumentSaveResponse(
  documentsToSave: Array<{
    toServerDocument: ToServerDocumentWithMetadata & {id: string};
    openVersion: number;
  }>,
  docResponse: {
    id: string;
    cidIdMap?: CidIdMap;
    cidNotePartProvenanceIdMap?: CidNotePartProvenanceIdMap;
    lock?: ServerDocumentEditorLock;
  }
): void {
  const documentId = docResponse.id;
  const documentData = documentsToSave.find((doc) => doc.toServerDocument.id === documentId);

  if (documentData) {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SAVE_COMPLETED,
      documentId,
      savedVersion: documentData.openVersion,
      versionId: docResponse.cidIdMap?.[documentData.toServerDocument.documentVersion.cid],
      cidIdMap: docResponse.cidIdMap,
      cidNotePartProvenanceIdMap: docResponse.cidNotePartProvenanceIdMap,
      lock: docResponse.lock,
      serverData: {},
    });

    if (DocumentStore.inStore(documentId)) {
      DocumentActions.fetchVersions(documentId);
    }
  }
}

/**
 * This is a Promise failure handler for any Document save actions. Its job is to show a friendly error as a
 * toast and then dispatch an action for each of the DocumentIds provided that the save failed.  This should
 * be the final `fail` handler on the Promise chain.
 */
function handleSaveFailure(
  deferredPromise: JQueryDeferred<boolean>,
  documentIds: Array<string>,
  response: Response
): void {
  const friendlyMessage = notifications.getFriendlyError(response);
  if (friendlyMessage != null) {
    notifications.error(friendlyMessage, {id: 'document-save-fail', duration: 0});
  }
  documentIds.forEach((documentId) => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SAVE_FAILED,
      documentId,
      error: response,
    });
    DocumentStore.markAllForUpdate(documentId);
  });
  // Resolve the deferred promise to signal that the save has failed.
  deferredPromise.resolve(false);
}

const DocumentActions = {
  close(documentId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_CLOSE,
      documentId,
    });
  },

  // Returns true if the document is successfully fetched from the server, false if the server returns an error.
  async open(
    documentId: string,
    {
      bumpVersion,
      disableAutoSaver,
      customAutoSaver,
      customSaveHandler,
      relatedDocumentIdGetter,
    }: DocumentOpenOptions = {}
  ): Promise<boolean> {
    // Use the getter from DocumentStore if one is not provided
    if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
      const effectiveRelatedDocumentIdGetter =
        relatedDocumentIdGetter || (() => DocumentStore.getRelatedDocumentIds(documentId));
      const relatedDocumentIds = effectiveRelatedDocumentIdGetter();
      if (relatedDocumentIds.length > 0) {
        return DocumentActions.openMany(relatedDocumentIds, {
          bumpVersion,
          disableAutoSaver,
          customAutoSaver,
          customSaveHandler,
          relatedDocumentIdGetter,
        });
      }
    }

    let didSucceed = false;
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_OPEN,
      documentId,
      disableAutoSaver,
      customAutoSaver,
      customSaveHandler,
      relatedDocumentIdGetter,
    });
    try {
      const document = await Promise.resolve(DocumentDAO.get(documentId));
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_OPEN_COMPLETED,
        documentId,
        document,
        bumpVersion,
      });
      if (bumpVersion) {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_UPDATE,
          documentId,
          op: {$merge: {}}, // NOOP
          bumpVersion: true,
        });
      }
      didSucceed = true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_OPEN_FAILED,
          documentId,
        });
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
    return didSucceed;
  },

  /**
   * Refreshes a document that already exists in the store.
   *
   * The difference between this and `open` is that this does not initialize the store with the document since
   * it is assumed that the document is already in the store.
   *
   * Returns true if the document is successfully refreshed, false if the server returns an error.
   */
  async refresh(documentId: string): Promise<boolean> {
    // Assert that the document already exists in the store
    if (!DocumentStore.inStore(documentId)) {
      throw new Error(`Document ${documentId} is not in store. Use DocumentActions.open() instead.`);
    }

    let didSucceed = false;

    try {
      const document = await Promise.resolve(DocumentDAO.get(documentId));
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_OPEN_COMPLETED,
        documentId,
        document,
      });
      didSucceed = true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_OPEN_FAILED,
          documentId,
        });
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
    return didSucceed;
  },

  /**
   * Batch-open multiple documents with a single API call. Mirrors the behaviour of `open` but
   * operates on an array of `documentIds` and utilises `DocumentDAO.getAll` for efficiency.
   *
   * Returns a promise that resolves to `true` when all documents are successfully fetched, or
   * `false` if the request fails.
   */
  async openMany(
    documentIds: Array<string>,
    {
      bumpVersion,
      disableAutoSaver,
      customAutoSaver,
      customSaveHandler,
      relatedDocumentIdGetter,
    }: DocumentOpenOptions = {}
  ): Promise<boolean> {
    if (documentIds.length === 0) {
      return true;
    }

    // Dispatch DOCUMENT_OPEN for each requested document so UI can reflect loading state.
    for (const documentId of documentIds) {
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_OPEN,
        documentId,
        disableAutoSaver,
        customAutoSaver,
        customSaveHandler,
        relatedDocumentIdGetter,
      });
    }

    try {
      const {documents} = await DocumentDAO.getAll(documentIds);

      // The response may not include every requested document (e.g. inaccessible); handle returned set.
      for (const document of documents) {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_OPEN_COMPLETED,
          documentId: document.id,
          document,
          bumpVersion,
        });

        if (bumpVersion) {
          // NOOP update to bump version – same behaviour as in `open`.
          AppDispatcher.dispatch({
            actionType: EventConstants.DOCUMENT_UPDATE,
            documentId: document.id,
            op: {$merge: {}},
            bumpVersion: true,
          });
        }
      }

      return true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        // Emit failure for each document so any listeners can react accordingly.
        for (const documentId of documentIds) {
          AppDispatcher.dispatch({
            actionType: EventConstants.DOCUMENT_OPEN_FAILED,
            documentId,
          });
        }
        notifications.notifyError(err, {extra: 'Failed to load all documents, please refresh the page.'});
        return false;
      }
      throw err; // Non-server failure – rethrow.
    }
  },

  save(documentId: string, dataModsHeaders?: DataModificationsHeader): JQueryPromise<boolean> {
    // Create a deferred promise that will be resolved once the save has completed.
    const deferredPromise = $.Deferred<boolean>();
    // If a save handler is provided to the Document ex: Via StepConfigEditor, use the save provided
    // rather than utilizing the Document PATCH route
    const saveImmediately = DocumentStore.getCustomSaveHandler(documentId);
    if (saveImmediately) {
      DocumentStore.resetSaveTracker(documentId);
      saveImmediately()
        .then(() => {
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        })
        .catch((error) => {
          DocumentStore.markAllForUpdate(documentId);
          notifications.notifyError(error);
          // Resolve the deferred promise to signal that the save has failed.
          deferredPromise.resolve(false);
        });
      return deferredPromise.promise();
    }
    // Saves the open document to the server.

    // This is saved here in case the document is removed from store before save finishes.
    // TODO(T1847): remove after adding refcounts
    const dataAtSaveTime = DocumentStore.getToServerDocument(documentId);

    benchlingUtil.asyncThrottle(`document-save-${documentId}`, () => {
      let dataToUse;
      if (DocumentStore.inStore(documentId)) {
        if (!DocumentStore.hasUnsavedChanges(documentId)) {
          return deferredPromise.resolve(false).promise();
        }
        dataToUse = DocumentStore.getToServerDocument(documentId);
      } else {
        dataToUse = dataAtSaveTime;
      }

      const {toServerDocument} = dataToUse;
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_SAVE,
        documentId,
      });
      const xhr = DocumentDAO.update(documentId, toServerDocument, dataModsHeaders)
        .then((response) => {
          if (!response) {
            return false;
          }

          processDocumentSaveResponse(
            [{...dataToUse, toServerDocument: {...toServerDocument, id: documentId}}],
            {
              ...response,
              id: documentId,
            }
          );
          return true;
        })
        .done((success) => {
          deferredPromise.resolve(success);
        })
        .fail((response) => {
          handleSaveFailure(deferredPromise, [documentId], response);
        });
      return xhr;
    });
    // Return the deferred promise so that the caller can wait for the save to complete.
    return deferredPromise.promise();
  },

  saveMany(documentIds: Array<string>): JQueryPromise<boolean> {
    // Create a deferred promise that will be resolved once the save has completed.
    const deferredPromise = $.Deferred<boolean>();

    // Filter to only documents that have unsaved changes and are in the store
    const documentsToSave = documentIds
      .filter(
        (documentId) => DocumentStore.inStore(documentId) && DocumentStore.hasUnsavedChanges(documentId)
      )
      .flatMap((documentId) => {
        const toServerDocument = DocumentStore.getToServerDocument(documentId);
        if (!toServerDocument) {
          return [];
        }
        return [
          {
            toServerDocument: {
              id: documentId,
              ...toServerDocument.toServerDocument,
            },
            openVersion: toServerDocument.openVersion, // Keep for local use in response handling
          },
        ];
      });

    // Prepare data for server - exclude openVersion field as it's not expected by the API
    const documentsForServer: Array<
      ToServerDocumentWithMetadata & {
        id: string;
      }
    > = documentsToSave.map(({toServerDocument}) => toServerDocument);

    if (documentsToSave.length === 0) {
      return $.Deferred<boolean>().resolve(true).promise();
    }

    // Dispatch save start events for all documents
    documentsForServer.forEach(({id}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_SAVE,
        documentId: id,
      });
    });

    DocumentDAO.bulkUpdate(documentsForServer)
      .then((response) => {
        if (!response?.responses) {
          return false;
        }
        response.responses.forEach((docResponse) => {
          processDocumentSaveResponse(documentsToSave, docResponse);
        });
        return true;
      })
      .done((success) => {
        // Resolve the deferred promise to signal that the save has completed.
        deferredPromise.resolve(success);
      })
      .fail((response) => {
        handleSaveFailure(deferredPromise, documentIds, response);
      });

    return deferredPromise.promise();
  },

  registerObjects(documentId: string, cidIdMap: CidIdMap): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_REGISTER_OBJECTS,
      documentId,
      cidIdMap,
    });
  },

  /**
   * Sets a name override for a document. This is necessary for Documents in Procedures Config that don't
   * have a real parent document name set. This should be called before the document is opened, and called
   * when the document is "renamed".
   */
  registerNameOverride(documentId: string, nameOverride: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_REGISTER_NAME_OVERRIDE,
      documentId,
      nameOverride,
    });
  },

  /** Directly updates the note of a given Document.
   *
   * If Table Mappings are provided, they will overwrite the existing ones.
   */
  updateNote(
    documentId: string,
    note: EntryNote,
    {
      structuredTableMappings,
      updatedItemPartIndexes,
      canIncrementalSave,
      performTableEvaluation,
    }: DocumentUpdateOptions = {}
  ): void {
    DocumentActions.update(
      documentId,
      DocumentUtil.entryVersionUpdate({
        $merge: {
          note,
          ...(structuredTableMappings && {
            structuredTableMappings,
          }),
        },
      }),
      {updatedItemPartIndexes, cidIdMap: null, bumpVersion: true, performTableEvaluation, canIncrementalSave}
    );
  },

  setName(documentId: string, name: string): void {
    DocumentActions.update(documentId, {$merge: {name}});
  },

  /**
   * Revert the document to a previous state
   */
  revert: (documentId: string, op: Spec<Document>, restoreData: SelectionRestoreData): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_REVERT,
      documentId,
      op,
      restoreData,
    });
  },

  loadVersion(documentId: string, versionId: number): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_LOAD_VERSION,
      documentId,
      versionId,
    });
    DocumentDAO.getVersion(documentId, versionId).then(
      (response: FromServerDocument) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_LOAD_VERSION_COMPLETED,
          documentId,
          versionId,
          version: response,
        });
      },
      () => {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_LOAD_VERSION_FAILED,
          documentId,
          versionId,
        });
      }
    );
  },

  fetchVersions(documentId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_FETCH_VERSIONS,
      documentId,
    });
    if (DocumentStore.inStore(documentId)) {
      DocumentDAO.getVersions(documentId).then(
        (response: DocumentsByVersion) => {
          AppDispatcher.dispatch(
            // eslint-disable-next-line no-restricted-syntax
            _.extend(
              {
                actionType: EventConstants.DOCUMENT_FETCH_VERSIONS_COMPLETED,
                documentId,
                versions: response.data,
              },
              pick(response, 'parent')
            )
          ); // parent key present with null value: the parent was deleted
          // omitted parent key: there is no parent.
        },
        () => {
          AppDispatcher.dispatch({
            actionType: EventConstants.DOCUMENT_FETCH_VERSIONS_FAILED,
            documentId,
          });
        }
      );
    } else {
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_FETCH_VERSIONS_FAILED,
        documentId,
      });
    }
  },

  /**
   * Applies `op` to the document with id `documentId`, and merges in the specified `cidIdMap`.
   * If `op` introduces new models, it should specify the ids in `cidIdMap`.
   */
  update(
    documentId: string,
    op: Spec<Document>,
    param?: {
      cidIdMap: CidIdMap | null;
      bumpVersion: boolean | null;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
      performTableEvaluation?: boolean;
    }
  ): void {
    // Attempt to acquire an editor lock prior to persisting the new document version
    DocumentEditorLockUtil.acquireLock(documentId);

    if (param == null) {
      param = {cidIdMap: null, bumpVersion: null};
    }
    const {cidIdMap, bumpVersion} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_UPDATE,
      documentId,
      op,
      cidIdMap: cidIdMap != null ? cidIdMap : {},
      bumpVersion: bumpVersion != null ? bumpVersion : true,
      updatedItemPartIndexes: param?.updatedItemPartIndexes,
      canIncrementalSave: param?.canIncrementalSave,
      performTableEvaluation: param?.performTableEvaluation,
    });
  },

  /**
   * Updates the document with the given spec and also updates all related documents
   * @param documentUpdate The update to the main document
   * @param relatedDocumentUpdates An array of updates to related documents
   * @param options
   */
  updateDocumentGroup: (
    documentUpdate: DocumentUpdateSpec,
    relatedDocumentUpdates?: Array<DocumentUpdateSpec>,
    options?: DocumentUpdateOptions
  ): void => {
    const {id: documentId} = documentUpdate;
    // Attempt to acquire an editor lock prior to persisting the new document version
    DocumentEditorLockUtil.acquireLock(documentId);
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_UPDATE_RELATED_DOCUMENTS,
      documentId,
      documentUpdate,
      relatedDocumentUpdates,
      options,
    });
  },

  postServerUpdate: (documentId: string, data: Partial<Document>): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_UPDATE,
      documentId,
      op: {$merge: data},
      bumpVersion: false,
    });
  },

  postServerSideUpdate: (documentId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SERVER_SIDE_UPDATE,
      documentId,
      bumpVersion: false,
    });
  },

  invalidate(documentId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_INVALIDATE,
      documentId,
    });
  },

  revalidate: (documentId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_OPEN_FAILED,
      documentId,
      // Since revalidate is called when an ActionFunction is aborted, the document will not be out of date,
      // unlike when we dispatch the same event when failing to reload or open the document.
      clearDirtyState: true,
    });
  },

  notifySaveStart: (updates: Array<{documentId: string}>): void => {
    for (const {documentId} of updates) {
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_SAVE,
        documentId,
      });
    }
  },

  notifySaveComplete: (
    updates: Array<{documentId: string; openVersion: number; documentVersionCid: string}>,
    cidIdMap: CidIdMap
  ): void => {
    for (const {documentId, openVersion, documentVersionCid} of updates) {
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_SAVE_COMPLETED,
        documentId,
        savedVersion: openVersion,
        versionId: cidIdMap[documentVersionCid],
        cidIdMap,
        serverData: {},
      });
    }
  },
};

export default DocumentActions;
